// Browser notifications helper.
// Requests permission on demand. Shows local Notification() which works when
// the tab is minimized or the browser is in the background (not fully closed).
// True app-closed push would need VAPID + service worker + a backend cron.

const _fired = new Set(); // key -> true, to avoid spamming duplicates

export function notificationsAllowed() {
  return typeof Notification !== 'undefined' && Notification.permission === 'granted';
}

export function notificationsBlocked() {
  return typeof Notification !== 'undefined' && Notification.permission === 'denied';
}

export async function requestNotificationsPermission() {
  if (typeof Notification === 'undefined') return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  try {
    const result = await Notification.requestPermission();
    return result;
  } catch {
    return 'denied';
  }
}

export function fireNotification(key, { title, body, icon = '/favicon.svg', tag, requireInteraction = false } = {}) {
  if (!notificationsAllowed()) return false;
  if (key && _fired.has(key)) return false;
  try {
    const n = new Notification(title, { body, icon, tag: tag || key, requireInteraction });
    n.onclick = () => { window.focus(); n.close(); };
    if (key) _fired.add(key);
    return true;
  } catch {
    return false;
  }
}

// Convenience for trading events
export function notifyStopLossHit(pos, price) {
  return fireNotification(`sl-${pos.id}`, {
    title: `Stop Loss hit · ${pos.display || pos.symbol}`,
    body: `${pos.side} ${pos.volume} closed at ${Number(price).toFixed(pos.precision || 2)} — SL was ${pos.sl}`,
    tag: `sl-${pos.id}`,
    requireInteraction: true
  });
}

export function notifyTakeProfitHit(pos, price) {
  return fireNotification(`tp-${pos.id}`, {
    title: `Take Profit hit · ${pos.display || pos.symbol}`,
    body: `${pos.side} ${pos.volume} closed at ${Number(price).toFixed(pos.precision || 2)} — TP was ${pos.tp}`,
    tag: `tp-${pos.id}`,
    requireInteraction: true
  });
}

export function notifyTrailingStopMoved(pos, oldSL, newSL) {
  return fireNotification(`trail-${pos.id}-${newSL.toFixed(4)}`, {
    title: `Trailing SL moved · ${pos.display || pos.symbol}`,
    body: `${pos.side} · SL ${Number(oldSL).toFixed(pos.precision || 2)} → ${Number(newSL).toFixed(pos.precision || 2)}`,
    tag: `trail-${pos.id}`
  });
}

export function clearFiredKey(key) {
  _fired.delete(key);
}
